import { lazy, Suspense, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { Route, Routes } from "react-router-dom";
import { Spinner } from "@moodstudios/farmacenter-design";
import { AppShell } from "./app/AppShell";
import { OrgProvider } from "./app/OrgContext";
import { TeclasProvider } from "./app/ui/teclas";
import { Entrar } from "./app/pages/Entrar";
import { Panel } from "./app/pages/Panel";
import { Asistente } from "./app/asistente/Asistente";

/**
 * Rutas con carga diferida. El Panel (portada), el login, la cáscara y Nova van en el
 * bundle inicial porque se ven siempre y de inmediato; el resto de pantallas —contabilidad,
 * caja, compras, configuración…— se cargan al navegar a ellas, para que el arranque no
 * arrastre código que la mayoría de sesiones no toca. Las páginas son exports con nombre, así
 * que cada import dinámico mapea su export a `default`, que es lo que `lazy` espera.
 */
const Existencias = lazy(() =>
  import("./app/pages/Existencias").then((m) => ({ default: m.Existencias })));
const FacturaCompra = lazy(() =>
  import("./app/pages/compras/FacturaCompra").then((m) => ({ default: m.FacturaCompra })));
const Facturas = lazy(() =>
  import("./app/pages/compras/Facturas").then((m) => ({ default: m.Facturas })));
const Ordenes = lazy(() =>
  import("./app/pages/compras/Ordenes").then((m) => ({ default: m.Ordenes })));
const Recepciones = lazy(() =>
  import("./app/pages/compras/Recepciones").then((m) => ({ default: m.Recepciones })));
const Borradores = lazy(() =>
  import("./app/pages/compras/Borradores").then((m) => ({ default: m.Borradores })));
const DetalleBorrador = lazy(() =>
  import("./app/pages/compras/Borradores").then((m) => ({ default: m.DetalleBorrador })));
const Actas = lazy(() =>
  import("./app/pages/compras/Actas").then((m) => ({ default: m.Actas })));
const DetalleActa = lazy(() =>
  import("./app/pages/compras/Actas").then((m) => ({ default: m.DetalleActa })));
const ConfiguracionCompras = lazy(() =>
  import("./app/pages/config/ConfiguracionCompras").then((m) => ({ default: m.ConfiguracionCompras })));
const Ventas = lazy(() =>
  import("./app/pages/ventas/Ventas").then((m) => ({ default: m.Ventas })));
const Documentos = lazy(() =>
  import("./app/pages/Documentos").then((m) => ({ default: m.Documentos })));
const Lotes = lazy(() =>
  import("./app/pages/inventario/Lotes").then((m) => ({ default: m.Lotes })));
const Asientos = lazy(() =>
  import("./app/pages/contabilidad/Asientos").then((m) => ({ default: m.Asientos })));
const PlanCuentas = lazy(() =>
  import("./app/pages/contabilidad/PlanCuentas").then((m) => ({ default: m.PlanCuentas })));
const ReportesContables = lazy(() =>
  import("./app/pages/contabilidad/ReportesContables").then((m) => ({ default: m.ReportesContables })));
const ComprobanteManual = lazy(() =>
  import("./app/pages/contabilidad/ComprobanteManual").then((m) => ({ default: m.ComprobanteManual })));
const ReglasCausacion = lazy(() =>
  import("./app/pages/contabilidad/ReglasCausacion").then((m) => ({ default: m.ReglasCausacion })));
const ReglasNodos = lazy(() =>
  import("./app/pages/contabilidad/ReglasNodos").then((m) => ({ default: m.ReglasNodos })));
const Sesiones = lazy(() =>
  import("./app/pages/caja/Sesiones").then((m) => ({ default: m.Sesiones })));
const Recibos = lazy(() =>
  import("./app/pages/caja/Recibos").then((m) => ({ default: m.Recibos })));
const Desembolsos = lazy(() =>
  import("./app/pages/caja/Desembolsos").then((m) => ({ default: m.Desembolsos })));
const Pagos = lazy(() =>
  import("./app/pages/cartera/Pagos").then((m) => ({ default: m.Pagos })));
const Configuracion = lazy(() =>
  import("./app/pages/Configuracion").then((m) => ({ default: m.Configuracion })));
const Catalogo = lazy(() =>
  import("./app/pages/config/Catalogo").then((m) => ({ default: m.Catalogo })));
const Terceros = lazy(() =>
  import("./app/pages/config/Terceros").then((m) => ({ default: m.Terceros })));
const ConfiguracionContable = lazy(() =>
  import("./app/pages/config/ConfiguracionContable").then((m) => ({ default: m.ConfiguracionContable })));
const EstadoPos = lazy(() =>
  import("./app/pages/pos/EstadoPos").then((m) => ({ default: m.EstadoPos })));
const Usuarios = lazy(() =>
  import("./app/pages/usuarios/Usuarios").then((m) => ({ default: m.Usuarios })));
const Roles = lazy(() =>
  import("./app/pages/usuarios/Roles").then((m) => ({ default: m.Roles })));

/**
 * La compuerta: nadie ve una pantalla del ERP sin sesión. El login no vive aquí
 * —se resuelve en Keycloak—, así que esto sólo decide entre esperar, mostrar el
 * error, invitar a entrar, o dejar pasar.
 */
export function App() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <Centrado><Spinner label="Comprobando la sesión…" /></Centrado>;
  }

  if (auth.error) {
    return (
      <Centrado>
        <div className="max-w-md text-center">
          <p className="font-brand text-xl font-bold text-danger">No se pudo iniciar sesión</p>
          <p className="mt-2 text-text-muted">{auth.error.message}</p>
        </div>
      </Centrado>
    );
  }

  if (!auth.isAuthenticated) {
    return <Entrar />;
  }

  return (
    <OrgProvider>
      <TeclasProvider>
      <AppShell>
        <Suspense fallback={<div className="grid h-full place-items-center p-6"><Spinner label="Cargando…" /></div>}>
          <Routes>
            <Route path="/" element={<Panel />} />
            <Route path="/existencias" element={<Existencias />} />
            <Route path="/compras/factura-nueva" element={<FacturaCompra />} />
            <Route path="/compras/facturas" element={<Facturas />} />
            <Route path="/compras/ordenes" element={<Ordenes />} />
            <Route path="/compras/recepciones" element={<Recepciones />} />
            <Route path="/compras/borradores" element={<Borradores />} />
            <Route path="/compras/borradores/:draftId" element={<DetalleBorrador />} />
            <Route path="/compras/actas" element={<Actas />} />
            <Route path="/compras/actas/:actId" element={<DetalleActa />} />
            <Route path="/ventas" element={<Ventas />} />
            <Route path="/documentos" element={<Documentos />} />
            <Route path="/inventario/lotes" element={<Lotes />} />
            <Route path="/contabilidad/asientos" element={<Asientos />} />
            <Route path="/contabilidad/plan-cuentas" element={<PlanCuentas />} />
            <Route path="/contabilidad/reportes" element={<ReportesContables />} />
            <Route path="/contabilidad/comprobante" element={<ComprobanteManual />} />
            <Route path="/contabilidad/reglas" element={<ReglasCausacion />} />
            <Route path="/contabilidad/reglas-nodos" element={<ReglasNodos />} />
            <Route path="/caja/sesiones" element={<Sesiones />} />
            <Route path="/caja/recibos" element={<Recibos />} />
            <Route path="/caja/desembolsos" element={<Desembolsos />} />
            <Route path="/cartera/pagos" element={<Pagos />} />
            <Route path="/configuracion" element={<Configuracion />} />
            <Route path="/configuracion/catalogo" element={<Catalogo />} />
            <Route path="/configuracion/terceros" element={<Terceros />} />
            <Route path="/configuracion/contabilidad" element={<ConfiguracionContable />} />
            <Route path="/configuracion/compras" element={<ConfiguracionCompras />} />
            <Route path="/usuarios" element={<Usuarios />} />
            <Route path="/roles" element={<Roles />} />
            <Route path="/pos/estado" element={<EstadoPos />} />
          </Routes>
        </Suspense>
      </AppShell>
      <Asistente />
      </TeclasProvider>
    </OrgProvider>
  );
}

function Centrado({ children }: { children: ReactNode }) {
  return <div className="grid min-h-screen place-items-center bg-surface-raised p-6">{children}</div>;
}
